import { Dropdown, type DropdownSelectProps } from '@sqs/rosetta-compositions';
import { Cell, TextInput } from '@sqs/rosetta-elements';
import { Text } from '@sqs/rosetta-primitives';
import React from 'react';
import { useI18n } from '../../i18n';

export interface DropdownSearchOption {
  value: number;
  label: string;
  displayOnSelect?: string;
  additionalSearch?: string[]; // long term, this could be an object of values that are hidden, but searchable
}

interface DropdownSearchProps extends DropdownSelectProps {
  readonly onChange: (value: any) => void;
  readonly dropdownSearchOptions: DropdownSearchOption[];
  readonly value: string | number;
  readonly emptyMessageOptions?: {
    empty?: string;
    match?: string;
  };
}

interface RenderTypeAheadProps {
  readonly isOpen: boolean;
  readonly onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  readonly setSearchValue: (v: string) => void;
  readonly searchValue: string;
  readonly selectedOption?: DropdownSearchOption;
  readonly setIsOpen: (v: boolean) => void;
  readonly variant: string;
}

const RenderDisplay = React.forwardRef(
  ({ isOpen, onKeyUp, searchValue, selectedOption, setIsOpen, setSearchValue, variant }: RenderTypeAheadProps, ref) => {
    const { t } = useI18n();

    const showOnSelect = selectedOption?.displayOnSelect ?? selectedOption?.label;
    const value = isOpen ? searchValue || showOnSelect : showOnSelect;

    return (
      <Cell
        ref={ref}
        variant={variant}
        body={
        <TextInput
          onChange={setSearchValue}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
            setIsOpen(true);
            onKeyUp(e);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={t("Select a User", {}, { project: 'enterprise-dashboard' })}
          value={value} />

        } />);


  }
);

const RenderBody = (props: any) => {
  return <Dropdown.Dropdown {...props} placement="bottom" focusLockIsDisabled />;
};

export const DropdownSearch = ({ dropdownSearchOptions, value, emptyMessageOptions, ...rest }: DropdownSearchProps) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  const selectedOption = dropdownSearchOptions.find((o) => o.value === value);
  const search = searchValue.toLowerCase();

  const renderedOptions = dropdownSearchOptions.
  filter((option) =>
  searchValue.length > 0 ?
  option.label.toLowerCase().includes(search) ||
  option.additionalSearch?.some((s) => s.toLowerCase().includes(search)) :
  true
  ).
  map((option) =>
  <Dropdown.Option key={option.value} value={option.value}>
        <Text.Body m={0}>{option.label}</Text.Body>
        {option.additionalSearch?.map((v) =>
    <Text.Body m={0} key={`${option.value}${v}`} fontSize={2}>
            {v}
          </Text.Body>
    )}
      </Dropdown.Option>
  );

  const noAvailabilityMessage =
  emptyMessageOptions?.empty ?? t("There are no options available", {}, { project: 'enterprise-dashboard' });
  const noMatchMessage =
  emptyMessageOptions?.match ?? t("No options match your search.", {}, { project: 'enterprise-dashboard' });

  const emptyMessage = searchValue.length > 0 ? noMatchMessage : noAvailabilityMessage;

  return (
    <Dropdown
      {...rest}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      renderDisplay={RenderDisplay}
      renderDropdown={RenderBody}
      value={value}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      selectedOption={selectedOption}>

      {renderedOptions.length ?
      renderedOptions :

      <Text.Body color="gray.300" px={2}>
          {emptyMessage}
        </Text.Body>
      }
    </Dropdown>);

};